/**
 * The recognitions feed never renders fewer than this many rows per tab before the cap line replaces
 * "Load more" (overview-feed-cap-plan.md "What changes"). It fires at AT LEAST 500 rows, never fewer
 * -- review C: rows arrive in `limit`-sized chunks (`atFeedCap` is only ever checked once a page has
 * landed), so the overshoot comes from PAGE GRANULARITY, not from the predicate's form. At `limit: 30`
 * the cap crosses mid-page at 510 held rows under BOTH the old `filter.limit * (filter.page + 1)`
 * counter and this HELD-ROW-COUNT form (feed-failed-page-plan.md, Option C) -- counting rows fixed the
 * failed-page divergence between what was requested and what actually landed, but it never changed
 * which page-sized increments the count can land on.
 */
export const RECOGNITION_FEED_ROW_CAP = 500;

/**
 * A second, independent predicate from `hasMore` (overview-feed-cap-plan.md "What changes").
 * `hasMore` answers "does the server have another page"; this answers "have we already taken as
 * much as we will show". Collapsing the two would make a function named for server state silently
 * encode a UI policy, and `hasMore`'s existing tests would start asserting two different things at
 * once.
 *
 * feed-failed-page-plan.md (Option C): counts the tab's own HELD ROW COUNT, not
 * `filter.limit * (filter.page + 1)`. `filter.page` means "the page last REQUESTED", which used to
 * diverge from what is actually on screen the moment a page request failed -- nothing rolled `page`
 * back, so this predicate could fire before the real cap, or fail to fire when it should. For THIS
 * predicate's own question -- how many rows does the tab actually hold right now -- the row array is
 * always honest about that count, whatever failed, which is exactly what removes the divergence. (It
 * is NOT honest as a page cursor for retrying a failed page -- that is
 * `receivedRecognitionsLoadedPages`/`sentRecognitionsLoadedPages`'s job instead; see
 * RecognitionState.interface.tsx.) On first render (no rows yet) this is still correctly false.
 *
 * @param rowCount - the tab's own held row count (`receivedRecognitions.length` /
 *   `sentRecognitions.length`), never a page/limit-derived count.
 * @returns true once the tab has loaded `RECOGNITION_FEED_ROW_CAP` rows or more.
 */
export const atFeedCap = (rowCount: number): boolean => rowCount >= RECOGNITION_FEED_ROW_CAP;

/**
 * The cap line's copy, hoisted to one place so both tabs render byte-identical text
 * (overview-feed-cap-plan.md "What the user sees") -- previously a verbatim literal duplicated in
 * both JSX branches with only the row count extracted, free to drift the moment either copy was
 * edited alone, exactly the failure `hasMore.ts` was extracted to prevent.
 *
 * Deliberately omits any "change the period" suggestion. `leaderboard.service.ts`'s
 * `getPeriodWindow` (:38-49) returns `{ from: startOf…, to: now }` for WEEK/MONTH/QUARTER and `null`
 * (no bound at all) for ALL_TIME -- the default AND the widest. Every other period is therefore a
 * strict SUBSET of the most-recent rows this capped feed already holds: changing it can only ever
 * show FEWER rows, never an older one. Do not add that clause back.
 *
 * The "Search by core value" sentence is opt-in via `searchAvailable`, never assumed -- the profile
 * recognitions tab (`UserRecognitionsContainer.tsx`) has NO search control at all (its only
 * `search`-shaped code is a sibling container's tab-routing `useSearchParams`, unrelated to this
 * list), so telling that screen's user to search would point at a control that is not on their
 * screen. This is the SAME defect class as the removed "change the period" clause above: naming a
 * control or an action the CURRENT screen cannot actually offer. `RecognitionList.tsx` (both
 * Received/Sent branches, which sit under the Overview's page-header search) passes `true`;
 * `UserRecognitionsContainer.tsx` (the profile tab, no search anywhere on screen) passes `false`.
 *
 * Names search's real scope, not an implied broader one, when it IS offered.
 * `recognition.service.ts`'s `addSearchToQuery` call (:90-93) matches `core_value.title` ONLY --
 * never the message text or either party's name.
 *
 * @param rowCount - the tab's OWN rendered row count (`receivedRecognitions.length` /
 *   `sentRecognitions.length`), never the constant cap. Interpolating the real count is what keeps the
 *   sentence true regardless of a failed page request in between (feed-failed-page-plan.md fixed the
 *   predicates themselves to read this same row count, so the two can no longer disagree either).
 * @param options.searchAvailable - whether THIS screen actually renders a search control. `false`
 *   drops the "Search by core value" sentence entirely instead of pointing at a control the screen
 *   does not have.
 * @returns the cap line's full sentence.
 */
export const feedCapMessage = (rowCount: number, options: { searchAvailable: boolean }): string =>
  options.searchAvailable
    ? `Showing the ${rowCount} most recent. Search by core value to find older ones.`
    : `Showing the ${rowCount} most recent.`;
